import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { I18nManager, Pressable, View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/AppText';
import Modal from 'react-native-modal';
import { ChevronLeft } from 'lucide-react-native';
import {
  KeyboardAvoidingView,
  KeyboardAwareScrollView,
  KeyboardStickyView,
  type KeyboardAwareScrollViewRef,
} from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '@tabbio/tokens';
import { useTheme } from '../../hooks/useTheme';
import { useHaptics } from '../../hooks/useHaptics';
import { SheetCloseButton } from './SheetCloseButton';

// Issue #295: KeyboardAwareScrollView only re-scrolls when the focused input is
// focused or changes its own height, not when content *above* it grows and
// pushes it under the keyboard (e.g. adding an achievement inserts a row above
// the "add achievement" field). Sheet content can call this to re-run the
// keep-the-focused-field-visible scroll after such a layout change.
const FormSheetKeyboardContext = createContext<() => void>(() => {});

/**
 * Re-runs the sheet's "scroll the focused field above the keyboard" pass.
 * Call after content inside the sheet grows/shrinks while a field is focused.
 * No-op outside a FormSheetModal.
 */
export function useAssureFocusedInputVisible() {
  return useContext(FormSheetKeyboardContext);
}

interface FormSheetModalProps {
  visible: boolean;
  title: string;
  onClose: () => void;
  onBack?: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  keyboardAvoiding?: boolean;
}

export function FormSheetModal({
  visible,
  title,
  onClose,
  onBack,
  children,
  footer,
  keyboardAvoiding = false,
}: FormSheetModalProps) {
  const { theme } = useTheme();
  const haptics = useHaptics();
  const insets = useSafeAreaInsets();
  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const assureFocusedInputVisible = useCallback(() => {
    // Two frames: the first lets React commit the new row, the second lets
    // native finish laying it out so the focused field measures at its new
    // position before the scroll view re-measures it.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollRef.current?.assureFocusedInputVisible?.();
      });
    });
  }, []);
  // The sticky footer floats above the keyboard, so a focused field must scroll
  // clear of BOTH the keyboard and the footer: offset by the measured footer
  // height (issue #33: with a fixed 24px offset the field landed behind Save).
  const [footerHeight, setFooterHeight] = useState(0);

  // Issue #169: react-native-modal keeps the sheet tree mounted while hidden, so
  // the scroll view kept its last offset and reopened part-way down the form
  // (e.g. at the End Date section) instead of at the first field. Bump a key on
  // every open→ so the scroll view remounts fresh at the top each time.
  const [instanceKey, setInstanceKey] = useState(0);
  const wasVisible = useRef(false);
  useEffect(() => {
    if (visible && !wasVisible.current) setInstanceKey((k) => k + 1);
    wasVisible.current = visible;
  }, [visible]);

  const sheet = (
    <View
      style={[
        styles.container,
        { backgroundColor: theme.background, paddingTop: insets.top },
      ]}
    >
      {/* Header: matches settings/CV formSheet pattern */}
      <View style={styles.header}>
        <View style={[styles.headerSide, styles.headerSideLeading]}>
          {onBack ? (
            <Pressable
              onPress={() => {
                haptics.trigger('light');
                onBack();
              }}
              hitSlop={12}
              accessibilityRole="button"
              style={({ pressed }) => ({ opacity: pressed ? 0.5 : 1 })}
            >
              <ChevronLeft
                size={24}
                color={theme.text}
                strokeWidth={2}
                style={
                  I18nManager.isRTL
                    ? { transform: [{ rotate: '180deg' }] }
                    : undefined
                }
              />
            </Pressable>
          ) : null}
        </View>
        <Text style={[styles.title, { color: theme.text }]}>{title}</Text>
        <View style={styles.headerSide}>
          <SheetCloseButton onClose={onClose} />
        </View>
      </View>

      {/* Content: issue #33: KeyboardAwareScrollView auto-scrolls the focused
            input (e.g. the multiline Overview field) above the soft keyboard. A
            plain ScrollView under KeyboardAvoidingView did not, so the field was
            covered while typing. */}
      <KeyboardAwareScrollView
        key={instanceKey}
        ref={scrollRef}
        style={styles.scroll}
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        showsVerticalScrollIndicator={false}
        bottomOffset={footerHeight + spacing[4]}
      >
        <FormSheetKeyboardContext.Provider value={assureFocusedInputVisible}>
          {children}
        </FormSheetKeyboardContext.Provider>
      </KeyboardAwareScrollView>

      {/* Sticky footer (e.g. save button): stays above the keyboard. */}
      {footer ? (
        keyboardAvoiding ? (
          <View
            onLayout={(e) => setFooterHeight(e.nativeEvent.layout.height)}
            style={[
              styles.footer,
              {
                borderTopColor: theme.border,
                paddingBottom: Math.max(spacing[4], insets.bottom),
              },
            ]}
          >
            {footer}
          </View>
        ) : (
          <KeyboardStickyView offset={{ opened: insets.bottom }}>
            <View
              onLayout={(e) => setFooterHeight(e.nativeEvent.layout.height)}
              style={[
                styles.footer,
                {
                  borderTopColor: theme.border,
                  paddingBottom: Math.max(spacing[4], insets.bottom),
                },
              ]}
            >
              {footer}
            </View>
          </KeyboardStickyView>
        )
      ) : null}
    </View>
  );

  return (
    <Modal
      isVisible={visible}
      onBackButtonPress={onClose}
      animationIn="slideInUp"
      animationOut="slideOutDown"
      hasBackdrop={false}
      coverScreen
      statusBarTranslucent
      useNativeDriver
      style={styles.rnModal}
    >
      {keyboardAvoiding ? (
        <KeyboardAvoidingView
          style={styles.container}
          behavior="padding"
          automaticOffset
        >
          {sheet}
        </KeyboardAvoidingView>
      ) : (
        sheet
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  rnModal: { margin: 0 },
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  headerSide: {
    width: 40,
    alignItems: 'flex-end',
  },
  headerSideLeading: {
    alignItems: 'flex-start',
  },
  title: {
    flex: 1,
    fontSize: 17,
    fontWeight: '600',
    textAlign: 'center',
  },
  scroll: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 24,
    gap: spacing[3],
  },
  footer: {
    paddingHorizontal: 20,
    paddingTop: spacing[3],
    paddingBottom: spacing[4],
    borderTopWidth: StyleSheet.hairlineWidth,
  },
});
